import { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import NumericInput from '@/components/NumericInput';
import type { KdsStation, CreateKdsStationData } from '@/types/Table.interface';

export interface KdsCategoryOption {
  id: number;
  name: string;
}

interface KdsStationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    data: CreateKdsStationData & { isActive?: boolean }
  ) => Promise<void>;
  station?: KdsStation | null;
  categories: KdsCategoryOption[];
  error?: string;
  nextSortOrder?: number;
}

const KdsStationDialog: FC<KdsStationDialogProps> = ({
  open,
  onClose,
  onSave,
  station,
  categories,
  error,
  nextSortOrder = 0
}) => {
  const { t } = useTranslation();
  const isEdit = !!station;

  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [isActive, setIsActive] = useState(true);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (station) {
      setName(station.name);
      setSortOrder(station.sortOrder);
      setIsActive(station.isActive);
      setCategoryIds(station.categoryIds ?? []);
    } else {
      setName('');
      setSortOrder(nextSortOrder);
      setIsActive(true);
      setCategoryIds([]);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [station, open]);

  const isValid = name.trim().length > 0;

  const handleSubmit = async () => {
    if (loading || !isValid) return;
    setLoading(true);
    try {
      await onSave({
        name: name.trim(),
        sortOrder,
        categoryIds,
        isActive
      });
    } catch {
      // The parent owns the server error state and renders it through `error`.
      // Swallow the rejection at the event boundary so a failed save is
      // recoverable without an unhandled promise rejection in the browser.
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const categoryName = (id: number) =>
    categories.find((c) => c.id === id)?.name ?? `#${id}`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? t('table.kds.station.edit') : t('table.kds.station.create')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label={t('table.kds.station.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            variant="outlined"
            inputProps={{ maxLength: 100 }}
          />

          <FormControl fullWidth>
            <InputLabel id="kds-station-categories-label">
              {t('table.kds.station.categories')}
            </InputLabel>
            <Select
              labelId="kds-station-categories-label"
              multiple
              value={categoryIds}
              onChange={(e) => {
                const value = e.target.value;
                setCategoryIds(
                  typeof value === 'string'
                    ? value.split(',').map(Number)
                    : (value as number[])
                );
              }}
              input={
                <OutlinedInput label={t('table.kds.station.categories')} />
              }
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id) => (
                    <Chip key={id} size="small" label={categoryName(id)} />
                  ))}
                </Box>
              )}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t('table.kds.station.categories.helper')}
            </FormHelperText>
          </FormControl>

          <NumericInput
            label={t('table.sort.order')}
            value={sortOrder}
            onChange={(val) => setSortOrder(val)}
            min={0}
            max={9999}
            allowDecimals={false}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label={t('table.is.active')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isValid}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KdsStationDialog;
